import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, Grid, InputAdornment, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Lock, Translate } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import { getLanguageReference, getMasters, type LanguageReferenceItem } from '../../services/platform/masterService';
import { provisionLanguage } from '../../services/platform/localeService';
import {
  LANGUAGE_FIELD_LABELS,
  LANGUAGE_MAX_SORT_ORDER,
  TRANSLATION_STATUS_META,
  nextLanguageSortOrder,
  normalizeLanguageName,
  normalizeLocaleInput,
  translationStatusOf,
  validateLanguageForm,
  type LanguageExistingRecord,
} from '../../utils/languageMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
  onRetranslateStarted?: (message: string) => void;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const count = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export default function LanguageFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
  onRetranslateStarted,
}: Props) {
  const queryClient = useQueryClient();
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [confirmRetranslate, setConfirmRetranslate] = useState(false);
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const meta = form.metadata;
  const code = str(form.code).toLowerCase();

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['language-reference'],
    queryFn: getLanguageReference,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'languages', 'form-siblings'],
    queryFn: () => getMasters('languages', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 0,
    refetchInterval: (query) => {
      const rows = (query.state.data?.data ?? []) as LanguageExistingRecord[];
      return rows.some((r) => r._id === editingId && r.metadata?.provisioning) ? 5000 : false;
    },
  });
  const existing = useMemo<LanguageExistingRecord[]>(
    () => ((existingRaw?.data ?? []) as LanguageExistingRecord[]),
    [existingRaw],
  );
  const current = editing ? existing.find((r) => r._id === editingId) : undefined;
  const liveMeta = (current?.metadata ?? meta) as Record<string, unknown>;

  const options = reference?.languages ?? [];
  const selected = useMemo<LanguageReferenceItem | null>(
    () => options.find((l) => l.code === code) || null,
    [options, code],
  );
  const takenCodes = useMemo(
    () => new Set(existing.filter((r) => r._id !== editingId).map((r) => str(r.code).toLowerCase())),
    [existing, editingId],
  );
  const suggestedSortOrder = useMemo(() => nextLanguageSortOrder(existing, editingId), [existing, editingId]);
  const rtl = selected?.rtl ?? meta.rtl === true;
  const isDefault = !!current?.isDefault || (!!reference && reference.defaultLanguage === code);
  const status = translationStatusOf(liveMeta);
  const statusMeta = TRANSLATION_STATUS_META[status];
  const isEnglish = code === 'en';

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', isActive: true, sortOrder: 0, metadata: { autoTranslate: true } });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateLanguageForm(
      { code, name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { existing, editingId, knownCode: editing ? !!code : !!selected },
    ),
    [code, form.name, form.description, form.sortOrder, meta, existing, editingId, editing, selected],
  );
  const blocking = Object.keys(errors).map((key) => [key, LANGUAGE_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading && !referenceLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const setMeta = (patch: Record<string, string | number | boolean>, ...keys: string[]) => {
    onChange({ ...form, metadata: { ...meta, ...patch } });
    touch(...(keys.length ? keys : Object.keys(patch)));
  };

  const applyLanguage = (option: LanguageReferenceItem | null) => {
    if (!option) {
      onChange({ ...form, code: '', name: '', metadata: { autoTranslate: meta.autoTranslate !== false } });
      return;
    }
    onChange({
      ...form,
      code: option.code,
      name: option.name,
      metadata: {
        autoTranslate: meta.autoTranslate !== false,
        locale: option.defaultLocale,
        nativeName: option.nativeName,
        rtl: option.rtl,
      },
    });
    touch('code', 'name', 'nativeName', 'locale');
  };

  const retranslate = useMutation({
    mutationFn: () => provisionLanguage(code, true, false),
    onSuccess: () => {
      setConfirmRetranslate(false);
      queryClient.invalidateQueries({ queryKey: ['masters', 'languages'] });
      onRetranslateStarted?.(`Re-translating every ${current?.name || code} string from English — this runs in the background.`);
    },
  });

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const actionLabel = editing ? 'Update' : 'Create';
  const localeOptions = selected?.locales ?? [];
  const translated = count(liveMeta.translatedCount);
  const untranslated = count(liveMeta.untranslatedCount);
  const total = count(liveMeta.provisionedCount);
  const provisionedAt = str(liveMeta.provisionedAt);

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Language`}
      subtitle="Pick an ISO 639-1 language — code, native name, locale and text direction fill in automatically."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={680}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={code ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!code}>
            {code ? `Complete these to enable ${actionLabel}:` : 'Start by picking a language from the ISO 639-1 list.'}
          </Typography>
          {code && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${label}: ${errors[key]}`}
                  color="warning"
                  variant="outlined"
                  onClick={() => focusField(key)}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                />
              ))}
            </Stack>
          )}
        </Alert>
      )}

      <FormSection title="Language" divider>
        <FormGrid>
          {!editing && (
            <Grid item xs={12} data-field="code">
              <Autocomplete<LanguageReferenceItem>
                options={options}
                loading={referenceLoading || existingLoading}
                value={selected}
                onChange={(_, value) => applyLanguage(value)}
                onBlur={() => touch('code')}
                getOptionLabel={(o) => `${o.name} (${o.code})`}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                getOptionDisabled={(o) => takenCodes.has(o.code)}
                filterOptions={(opts, s) => {
                  const q = s.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((o) => o.name.toLowerCase().includes(q)
                    || o.code === q
                    || o.nativeName.toLowerCase().includes(q));
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.code}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={700} sx={{ width: 32 }}>{o.code}</Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary" dir={o.rtl ? 'rtl' : 'ltr'}>{o.nativeName}</Typography>
                      {o.rtl && <Chip size="small" label="RTL" variant="outlined" />}
                      {takenCodes.has(o.code) && <Chip size="small" label="Already added" />}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="small"
                    label="Language (ISO 639-1)"
                    placeholder="Search by English or native name, or code"
                    error={!!errorFor('code')}
                    helperText={errorFor('code') || `${options.length ? `${options.length - takenCodes.size} languages available` : 'Loading languages…'} · already added ones are disabled`}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Language name (English)"
              value={form.name}
              disabled={!code}
              onChange={(e) => {
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeLanguageName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || `Unique · 2–60 characters${selected && selected.name !== normalizeLanguageName(form.name) ? ` · ISO name: ${selected.name}` : ''}`}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={code}
              helperText={editing ? 'Locked' : 'Auto from language'}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Text direction"
              value={code ? (rtl ? 'Right-to-left' : 'Left-to-right') : ''}
              helperText="Auto from the language script"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="nativeName">
            <TextField
              fullWidth
              size="small"
              required
              label="Native name"
              value={str(meta.nativeName) === '' ? '' : String(meta.nativeName)}
              disabled={!code}
              onChange={(e) => setMeta({ nativeName: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') })}
              onBlur={() => { touch('nativeName'); setMeta({ nativeName: normalizeLanguageName(meta.nativeName) }); }}
              error={!!errorFor('nativeName')}
              helperText={errorFor('nativeName')
                || (selected && selected.nativeName !== normalizeLanguageName(meta.nativeName)
                  ? `Standard native name: ${selected.nativeName}`
                  : 'Shown in the language switcher — written in the language itself')}
              FormHelperTextProps={{ sx: selected && selected.nativeName !== normalizeLanguageName(meta.nativeName) && !errorFor('nativeName') ? { color: 'warning.main' } : undefined }}
              inputProps={{ maxLength: 60, dir: rtl ? 'rtl' : 'ltr' }}
              InputProps={{
                endAdornment: selected && selected.nativeName !== normalizeLanguageName(meta.nativeName) ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setMeta({ nativeName: selected.nativeName })}>Reset</Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="locale">
            <Autocomplete<string, false, false, true>
              freeSolo
              options={localeOptions}
              value={str(meta.locale)}
              disabled={!code}
              onChange={(_, value) => setMeta({ locale: normalizeLocaleInput(value || '') }, 'locale')}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setMeta({ locale: value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 20) }, 'locale');
              }}
              onBlur={() => { touch('locale'); setMeta({ locale: normalizeLocaleInput(str(meta.locale)) }, 'locale'); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Locale (BCP 47)"
                  placeholder={selected?.defaultLocale || 'en-US'}
                  error={!!errorFor('locale')}
                  helperText={errorFor('locale')
                    || `language-REGION, e.g. ${selected?.defaultLocale || 'en-US'} · used for dates and numbers`}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Order in the language switcher · unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, max: LANGUAGE_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {isDefault
                ? 'This is the default language — it cannot be deactivated until another default is chosen in Locale settings.'
                : editing
                  ? 'Use the status toggle in the Languages list to activate or deactivate. Inactive languages are hidden from the language switcher.'
                  : 'New languages are created as Active and appear in the language switcher right away.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Translations">
        <FormGrid>
          {!isEnglish && (
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={meta.autoTranslate !== false}
                    disabled={!code}
                    onChange={(e) => setMeta({ autoTranslate: e.target.checked })}
                  />
                )}
                label="Auto-translate on add / activate"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                {meta.autoTranslate !== false
                  ? `Machine-translates every English admin and mobile string into ${form.name || 'this language'} in the background${selected?.hasCatalog ? ' (a reviewed catalog is available and is used first)' : ''}. Existing translations are kept.`
                  : 'Off — add translations in Translator Master. Until then every string is shown in English.'}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Typography variant="body2" fontWeight={600}>Translation status</Typography>
                <Chip size="small" color={statusMeta.color} label={editing ? statusMeta.label : 'Starts after create'} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>Set by the system</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                {editing
                  ? statusMeta.description
                  : isEnglish
                    ? 'English is the source language — no translation needed.'
                    : meta.autoTranslate !== false
                      ? 'Translation starts as soon as the language is created. Users see English for any string not translated yet.'
                      : 'Users who pick this language see English until translations are added.'}
              </Typography>
              {editing && (translated != null || total != null) && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {translated != null ? `${translated} translated` : `${total} strings`}
                  {untranslated ? ` · ${untranslated} still in English` : ''}
                  {provisionedAt ? ` · last run ${new Date(provisionedAt).toLocaleString()}` : ''}
                </Typography>
              )}
              {editing && str(liveMeta.provisionError) && status === 'failed' && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{str(liveMeta.provisionError)}</Typography>
              )}
            </Paper>
          </Grid>
          {editing && !isEnglish && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<Translate />}
                disabled={status === 'translating' || retranslate.isPending || current?.isActive === false}
                onClick={() => setConfirmRetranslate(true)}
              >
                Re-translate all strings
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {status === 'translating'
                  ? 'A translation job is already running.'
                  : current?.isActive === false
                    ? 'Activate the language first.'
                    : 'Runs immediately — separate from Update.'}
              </Typography>
              {retranslate.isError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {(retranslate.error as { response?: { data?: { message?: string } } })?.response?.data?.message
                    || 'Could not start re-translation'}
                </Alert>
              )}
            </Grid>
          )}
        </FormGrid>
      </FormSection>

      <Dialog open={confirmRetranslate} onClose={() => !retranslate.isPending && setConfirmRetranslate(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Re-translate all {current?.name || code} strings?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            Every admin and mobile string will be machine-translated again from English.
            <strong> This overwrites existing {current?.name || code} translations, including manual edits made in Translator Master.</strong>
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ mt: 1.5 }}>
            The job runs in the background; current translations stay visible until each one is replaced.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRetranslate(false)} disabled={retranslate.isPending}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => retranslate.mutate()} disabled={retranslate.isPending}>
            {retranslate.isPending ? 'Starting…' : 'Overwrite & re-translate'}
          </Button>
        </DialogActions>
      </Dialog>
    </FormDrawer>
  );
}
